import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import NavBar from '../common/NavBar';
import Footer from '../Footer';
import { useIsMobile } from '../../Utils/useIsMobile';
import MobileNavBar from '../common/MobileNavBar';
import MobileFooter from '../MobileFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import KakaoChannelModal from '../common/KakaoChannelModal';
import ProfileIncompleteModal from '../common/ProfileIncompleteModal';
import {
  FOOTER_BREAKPOINT,
  MOBILE_BREAKPOINT,
  convertToVwMob,
} from '../../Utils/responsive';
import { PATHS } from '../../routes/paths';
import postIcon from '../../assets/Icon/sidbar/post_icon.png';
import postActiveIcon from '../../assets/Icon/sidbar/post_active_icon.png';
import sponsorIcon from '../../assets/Icon/sidbar/sponsor_icon.png';
import sponsorActiveIcon from '../../assets/Icon/sidbar/sponsor_active_icon.png';
import payIcon from '../../assets/Icon/sidbar/pay_icon.png';
import payActiveIcon from '../../assets/Icon/sidbar/pay_active_icon.png';
import projectIcon from '../../assets/Icon/sidbar/project_icon.png';
import projectActiveIcon from '../../assets/Icon/sidbar/project_active_icon.png';

const KAKAO_MODAL_SHOWN_STORAGE_KEY = 'missionfund.kakaoModalSeenAfterLogin';

type ProjectSidebarActive = 'posts' | 'donors' | 'payments' | 'settings';
const PROJECT_SIDEBAR_ACTIVE_COLOR = '#2BC2E1';

type Props = {
  title?: string;
  children: ReactNode;
  rightLinks?: ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
  sidebarActive?: ProjectSidebarActive;
  projectId?: string;
};

const WebPageLayout: React.FC<Props> = ({
  title,
  children,
  rightLinks,
  hideHeader,
  hideFooter,
  sidebarActive,
  projectId: propProjectId,
}) => {
  const isMobileNav = useIsMobile(MOBILE_BREAKPOINT);
  const isMobileFooter = useIsMobile(FOOTER_BREAKPOINT);
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = useAppSelector(selectUserProfile);
  const [isKakaoModalOpen, setIsKakaoModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  useEffect(() => {
    // 로그인, 회원가입 페이지에서는 카카오/프로필 모달 띄우지 않음
    if (location.pathname === '/login' || location.pathname === '/signup') {
      setIsKakaoModalOpen(false);
      setIsProfileModalOpen(false);
      return;
    }

    // 1. sns_id 체크 -> 없으면 카카오 모달 표시
    // 2. 카카오 모달이 닫히거나, sns_id가 있는 경우 -> 프로필 정보 체크
    // 3. 프로필 정보가 불충분하면 -> 프로필 미완료 모달 표시
    
    if (userProfile) {
      if (!userProfile.sns_id) {
        let shouldOpen = true;
        if (typeof window !== 'undefined') {
          const hasShown = localStorage.getItem(KAKAO_MODAL_SHOWN_STORAGE_KEY) === '1';
          shouldOpen = !hasShown;
        }
        if (shouldOpen) {
          if (typeof window !== 'undefined') {
            localStorage.setItem(KAKAO_MODAL_SHOWN_STORAGE_KEY, '1');
          }
          setIsKakaoModalOpen(true);
        } else {
          setIsKakaoModalOpen(false);
          checkProfileCompleteness();
        }
      } else {
        setIsKakaoModalOpen(false);
        checkProfileCompleteness();
      }
    } else {
      // 로그아웃 상태 등 userProfile이 없으면 모달 닫기
      setIsKakaoModalOpen(false);
      setIsProfileModalOpen(false);
    }
  }, [userProfile, location.pathname]);

  const checkProfileCompleteness = () => {
    // 프로필 정보가 불충분한지 확인하는 로직 (index.tsx의 verificationStatus === 'none'과 유사)
    // authentication_by가 없고, authentication_image_url도 없고, registration_card_url도 없는 경우
    // 즉, 아직 인증 자료를 제출하지 않은 상태
    
    if (userProfile) {
       // 간단하게 필수 정보가 누락되었는지 확인 (여기서는 인증 관련 필드 기준)
       // 인증 완료: authentication_by 존재
       // 인증 진행중: authentication_image_url 또는 registration_card_url 존재
       // 인증 전(none): 위 조건 모두 불만족
       
       const isComplete = !!userProfile.authentication_by;
       const isInProgress = !!(userProfile.authentication_image_url || userProfile.registration_card_url);
       
       if (!isComplete && !isInProgress) {
         // 현재 페이지가 이미 /account 인지 확인하여 중복 모달 방지
         if (location.pathname !== '/account') {
            setIsProfileModalOpen(true);
         }
       } else {
         setIsProfileModalOpen(false);
       }
    }
  };

  const handleCloseKakaoModal = () => {
    setIsKakaoModalOpen(false);
    // 카카오 모달 닫으면 바로 프로필 체크
    checkProfileCompleteness();
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  const currentProjectId = propProjectId || (location.state as { projectId?: string } | undefined)?.projectId;

  const showProjectSidebar = Boolean(sidebarActive) && !isMobileNav;

  const projectSidebarItems: Array<{
    key: ProjectSidebarActive;
    label: string;
    path: string;
    icon: string;
    activeIcon: string;
  }> = [
    {
      key: 'posts',
      label: '게시물 관리',
      path: PATHS.POST_MANAGEMENT,
      icon: postIcon,
      activeIcon: postActiveIcon,
    },
    {
      key: 'donors',
      label: '후원자 관리',
      path: PATHS.DONOR_MANAGEMENT,
      icon: sponsorIcon,
      activeIcon: sponsorActiveIcon,
    },
    {
      key: 'payments',
      label: '결제현황',
      path: PATHS.PAYMENT_STATUS,
      icon: payIcon,
      activeIcon: payActiveIcon,
    },
    {
      key: 'settings',
      label: '프로젝트 설정',
      path: PATHS.PROJECT_SETTINGS,
      icon: projectIcon,
      activeIcon: projectActiveIcon,
    },
  ];

  return (
    <PageBackground>
      <Shell>
        {!hideHeader &&
          (isMobileNav ? (
            <MobileNavBar title={title} />
          ) : (
            <NavBar title={title} rightLinks={rightLinks} />
          ))}
        <Main $hasSidebar={showProjectSidebar}>
          <Content $hasSidebar={showProjectSidebar}>
            {showProjectSidebar ? (
              <ProjectShell>
                <ProjectSidebar>
                  <SidebarMenu>
                    {projectSidebarItems.map((it) => {
                      const active = sidebarActive === it.key;
                      return (
                        <SidebarItem
                          key={it.key}
                          type="button"
                          $active={active}
                          onClick={() => navigate(it.path, { state: { projectId: currentProjectId } })}
                        >
                          <ItemIconImg
                            src={active ? it.activeIcon : it.icon}
                            alt=""
                            aria-hidden="true"
                          />
                          <ItemText $active={active}>{it.label}</ItemText>
                        </SidebarItem>
                      );
                    })}
                  </SidebarMenu>
                </ProjectSidebar>
                <ProjectMain>{children}</ProjectMain>
              </ProjectShell>
            ) : (
              children
            )}
          </Content>
        </Main>
        {!hideFooter && (isMobileFooter ? <MobileFooter /> : <Footer />)}
      </Shell>
      <KakaoChannelModal isOpen={isKakaoModalOpen} onClose={handleCloseKakaoModal} />
      <ProfileIncompleteModal isOpen={isProfileModalOpen} onClose={handleCloseProfileModal} />
    </PageBackground>
  );
};

const PageBackground = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.grayScale[3]};
`;

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main<{ $hasSidebar?: boolean }>`
  flex: 1;
  display: flex;
  justify-content: ${({ $hasSidebar }) => ($hasSidebar ? 'flex-start' : 'center')};
  padding: 16px 0;
  padding-left: ${({ $hasSidebar }) => ($hasSidebar ? '47px' : '0')};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${convertToVwMob(16)} ${convertToVwMob(0)};
    justify-content: stretch;
    padding-left: 0;
  }
`;

const Content = styled.div<{ $hasSidebar?: boolean }>`
  width: 100%;
  max-width: 1200px;
  min-height: 70vh;
  padding: ${({ $hasSidebar }) => ($hasSidebar ? '0 16px 0 0' : '0 16px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.colors.grayScale[80]};

  @media (max-width: ${FOOTER_BREAKPOINT}px) {
    padding: ${({ $hasSidebar }) => ($hasSidebar ? '0 12px 0 0' : '0 12px')};
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-height: auto;
    padding: 0;
    gap: ${convertToVwMob(12)};
  }
`;

const ProjectShell = styled.div`
  width: 100%;
  display: flex;
  gap: 88px;
  align-items: flex-start;
`;

const ProjectSidebar = styled.aside`
  width: 240px;
  min-width: 240px;
  background: transparent;
  border-radius: 0;
  border: none;
  padding: 52px 12px 32px 0;
  box-sizing: border-box;
`;

const ProjectMain = styled.section`
  flex: 1;
  min-width: 0;
  border-radius: 16px;
  box-sizing: border-box;
`;

const SidebarMenu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;

`;

const SidebarItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  text-align: left;

  background: transparent;
  box-shadow: none;
  border-left: none;

  &:hover {
    background: ${({ theme }) => theme.colors.grayScale[2]};
  }
`;

const ItemIconImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const ItemText = styled.span<{ $active?: boolean }>`
  ${({ theme }) => theme.typography.contents.medium20}
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ theme, $active }) =>
    $active ? PROJECT_SIDEBAR_ACTIVE_COLOR : theme.colors.grayScale[90]};
`;

export default WebPageLayout;

